import { FC, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useEndpoints } from "../../../hooks/useEndpoints";
import { XIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import style from "./HooperHistoryModal.module.scss";

export type HooperMetric =
  | "fatigue"
  | "sleepQuality"
  | "muscleSoreness"
  | "stressLevel"
  | "mood"
  | "average";

const METRIC_CONFIG: Record<HooperMetric, { label: string; color: string }> = {
  fatigue: { label: "Fatica", color: "#f57c73" },
  sleepQuality: { label: "Qualità Sonno", color: "#5BC9E0" },
  muscleSoreness: { label: "Dolore muscoli", color: "#FF9F43" },
  stressLevel: { label: "Stress", color: "#013344" },
  mood: { label: "Umore", color: "#9747FF" },
  average: { label: "Media Hooper", color: "#27ae60" },
};

type Period = "7d" | "30d" | "90d";

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: "7d", label: "7G" },
  { value: "30d", label: "30G" },
  { value: "90d", label: "3M" },
];

function computeAverage(q: any): number | null {
  const keys = ["fatigue", "sleepQuality", "muscleSoreness", "stressLevel", "mood"];
  const values = keys.map((k) => q[k]).filter((v) => v != null);
  if (values.length === 0) return null;
  return values.reduce((sum: number, v: number) => sum + v, 0) / values.length;
}

function getMetricValue(q: any, metric: HooperMetric): number | null {
  if (metric === "average") return computeAverage(q);
  const v = q[metric];
  return v != null ? Number(v) : null;
}

interface HooperHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  athleteId: string | null;
  athleteName: string | null;
  metric: HooperMetric | null;
}

const HooperHistoryModal: FC<HooperHistoryModalProps> = ({
  isOpen,
  onClose,
  athleteId,
  athleteName,
  metric,
}) => {
  const [period, setPeriod] = useState<Period>("30d");
  const endpoints = useEndpoints();

  const { startDate, endDate } = useMemo(() => {
    const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
    return {
      startDate: dayjs().subtract(days, "day").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    };
  }, [period]);

  const query = useQuery({
    queryKey: ["hooperHistory", athleteId, period],
    queryFn: async () => {
      const res = await endpoints.getSubjectReadiness({
        userId: athleteId!,
        startDate,
        endDate,
      });
      return res.data;
    },
    enabled: isOpen && !!athleteId && !!metric,
  });

  const chartData = useMemo(() => {
    if (!query.data || !metric) return [];
    const athlete = query.data[0];
    if (!athlete) return [];
    const questionnaires = Array.isArray(athlete.hooperQuestionnaire)
      ? athlete.hooperQuestionnaire
      : athlete.hooperQuestionnaire
      ? [athlete.hooperQuestionnaire]
      : [];
    return [...questionnaires]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((q) => {
        const value = getMetricValue(q, metric);
        return value !== null
          ? { date: q.date, value: Number(value.toFixed(2)) }
          : null;
      })
      .filter((d): d is { date: string; value: number } => d !== null);
  }, [query.data, metric]);

  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    const values = chartData.map((d) => d.value);
    const avg = values.reduce((s, v) => s + v, 0) / values.length;
    return {
      avg: avg.toFixed(1),
      min: Math.min(...values).toFixed(1),
      max: Math.max(...values).toFixed(1),
    };
  }, [chartData]);

  if (!isOpen || !metric || !athleteId) return null;

  const config = METRIC_CONFIG[metric];

  return (
    <div className={style.overlay} onClick={onClose}>
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <div className={style.header}>
          <div>
            <h2 className={style.title}>
              {config.label}
              {athleteName ? ` — ${athleteName}` : ""}
            </h2>
            <p className={style.subtitle}>Storico questionario soggettivo</p>
          </div>
          <button className={style.closeBtn} onClick={onClose}>
            <XIcon size={20} />
          </button>
        </div>

        <div className={style.periodBar}>
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`${style.periodBtn} ${
                period === opt.value ? style.periodBtnActive : ""
              }`}
              onClick={() => setPeriod(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className={style.body}>
          {query.isFetching ? (
            <div className={style.skeleton}>
              <div
                className={style.skeletonBar}
                style={{ width: "40%", height: 14, marginBottom: 16 }}
              />
              <div
                className={style.skeletonBar}
                style={{ width: "100%", height: 240 }}
              />
            </div>
          ) : chartData.length === 0 ? (
            <p className={style.emptyState}>
              Nessun dato nel periodo selezionato
            </p>
          ) : (
            <>
              {stats && (
                <div className={style.statsRow}>
                  <div className={style.statItem}>
                    <span className={style.statLabel}>Media</span>
                    <span
                      className={style.statValue}
                      style={{ color: config.color }}
                    >
                      {stats.avg}
                    </span>
                  </div>
                  <div className={style.statDivider} />
                  <div className={style.statItem}>
                    <span className={style.statLabel}>Min</span>
                    <span className={style.statValue}>{stats.min}</span>
                  </div>
                  <div className={style.statDivider} />
                  <div className={style.statItem}>
                    <span className={style.statLabel}>Max</span>
                    <span className={style.statValue}>{stats.max}</span>
                  </div>
                </div>
              )}

              <div className={style.chartWrap}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 16, left: -10, bottom: 30 }}
                  >
                    <defs>
                      <linearGradient
                        id={`grad-${metric}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={config.color}
                          stopOpacity={0.25}
                        />
                        <stop
                          offset="95%"
                          stopColor={config.color}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e7e7e7"
                      vertical={false}
                    />
                    {stats && (
                      <ReferenceLine
                        y={Number(stats.avg)}
                        stroke={config.color}
                        strokeDasharray="6 3"
                        strokeWidth={1}
                      />
                    )}
                    <XAxis
                      dataKey="date"
                      tick={{
                        fill: "#4d4d4d",
                        fontSize: 11,
                        fontFamily: "'Satoshi', sans-serif",
                      }}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tickFormatter={(d) => dayjs(d).format("DD/MM")}
                    />
                    <YAxis
                      domain={[0, 6]}
                      ticks={[1, 2, 3, 4, 5]}
                      tick={{
                        fill: "#4d4d4d",
                        fontSize: 11,
                        fontFamily: "'Satoshi', sans-serif",
                      }}
                      tickLine={false}
                      axisLine={false}
                      width={35}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e7e7e7",
                        borderRadius: "8px",
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "12px",
                        padding: "8px 12px",
                      }}
                      formatter={(value: any) => [value, config.label]}
                      labelFormatter={(d) => dayjs(d).format("DD/MM/YYYY")}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={config.color}
                      strokeWidth={3}
                      fill={`url(#grad-${metric})`}
                      dot={{ fill: config.color, r: 4, strokeWidth: 2, stroke: "#fff" }}
                      activeDot={{ r: 6 }}
                      connectNulls
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HooperHistoryModal;
